<template>
    <main class="home-page">
        <TopHeader />
        <div class="home-scroll-progress" :style="{ transform: `scaleX(${scrollProgress})` }" aria-hidden="true"></div>

        <section id="hero" class="home-hero section-reveal">
            <video class="hero-media" src="@/assets/vids/home_vid_4.mp4" autoplay muted loop playsinline
                aria-label="AI voice creation preview" ref="heroVideo" @loadeddata="onHeroVideoLoaded"></video>
            <img class="hero-media hero-poster" src="@/assets/figs/home/home_hero_2.png" alt=""
                v-show="!heroVideoLoaded" />
            <div class="hero-vignette" aria-hidden="true"></div>
            <div class="hero-copy">
                <p class="eyebrow">AI voice creation toolkit</p>
                <h1>
                    <span class="hero-title-copy-line"><span>Every voice project,</span></span>
                    <span class="hero-title-copy-line"><span>beautifully produced.</span></span>
                </h1>
                <p class="hero-subtitle">
                    Create, design, organize, and export studio-quality voice audio from one calm, focused workspace.
                </p>
                <div class="hero-actions">
                    <button class="primary-link" @click="goToFeature('/text-to-speech-projects', 'Text to Speech')">
                        Start producing
                    </button>
                    <button class="secondary-link" @click="goToFeature('/voice-gallery', 'Voice Gallery')">
                        See voices
                    </button>
                </div>
            </div>
        </section>

        <section id="gallery" class="spotlight spotlight-light section-reveal">
            <div class="spotlight-copy">
                <p class="eyebrow">Voice Gallery</p>
                <h2>Find the right voice before the first line is written.</h2>
                <p>
                    Browse polished voice profiles, compare styles, and move from discovery to production without
                    breaking flow.
                </p>
            </div>
            <figure class="gallery-stage" aria-label="Voice Gallery interface preview">
                <div class="recommend-panel">
                    <div class="recommend-topline">
                        <span>Voice recommendations</span>
                        <strong>Suggestions</strong>
                    </div>
                    <p class="recommend-prompt">"Warm narrator for an audiobook trailer"</p>
                    <div class="recommend-list">
                        <button class="recommend-row" @click="goToFeature('/voice-gallery', 'Voice Gallery')">
                            <img :src="kokoroAvatar('bf_isabella')" alt="" />
                            <div>
                                <strong>Isabella</strong>
                                <span>British narrator</span>
                            </div>
                            <em>Top pick</em>
                        </button>
                        <button class="recommend-row" @click="goToFeature('/voice-gallery', 'Voice Gallery')">
                            <img :src="kokoroAvatar('af_sky')" alt="" />
                            <div>
                                <strong>Sky</strong>
                                <span>Clear and bright</span>
                            </div>
                            <em>Bright</em>
                        </button>
                        <button class="recommend-row" @click="goToFeature('/voice-gallery', 'Voice Gallery')">
                            <img :src="kokoroAvatar('bm_fable')" alt="" />
                            <div>
                                <strong>Fable</strong>
                                <span>Warm storyteller</span>
                            </div>
                            <em>Warm</em>
                        </button>
                    </div>
                </div>

                <div class="voice-browser">
                    <div class="voice-browser-header">
                        <strong>Possible voices</strong>
                        <span>Basic collection</span>
                    </div>
                    <article class="featured-voice">
                        <img :src="kokoroAvatar('bf_isabella')" alt="" />
                        <div class="featured-voice-copy">
                            <div>
                                <span class="featured-label">Recommended voice</span>
                                <h3>Isabella</h3>
                                <p>Warm British narration for polished long-form audio.</p>
                            </div>
                            <div class="voice-tags" aria-label="Isabella voice traits">
                                <span>British</span>
                                <span>Female</span>
                                <span>Warm</span>
                            </div>
                        </div>
                        <button @click="goToFeature('/voice-gallery', 'Voice Gallery')">Preview</button>
                    </article>
                    <div class="voice-grid">
                        <article v-for="voice in possibleVoices" :key="voice.name" class="voice-card"
                            @click="goToFeature('/voice-gallery', 'Voice Gallery')">
                            <img :src="voice.image" alt="" />
                            <div>
                                <h3>{{ voice.name }}</h3>
                                <p>{{ voice.description }}</p>
                            </div>
                            <button :aria-label="`Preview ${voice.name}`" @click.stop="goToFeature('/voice-gallery', 'Voice Gallery')">
                                <span class="play-triangle" aria-hidden="true"></span>
                            </button>
                        </article>
                        <article class="voice-card more-voices-card" @click="goToFeature('/voice-gallery', 'Voice Gallery')">
                            <div>
                                <h3>More voices</h3>
                                <p>Open the full gallery</p>
                            </div>
                            <button aria-label="Open more voices" @click.stop="goToFeature('/voice-gallery', 'Voice Gallery')">
                                +
                            </button>
                        </article>
                    </div>
                </div>
            </figure>
        </section>

        <section id="designer" class="cinema section-reveal">
            <div class="cinema-copy">
                <p class="eyebrow">Voice Designer</p>
                <h2>Shape a new voice from a reference and a direction.</h2>
                <p>Start with a base voice, add creative intent, and preview the result in a space made for listening.
                </p>
            </div>
            <div class="designer-workflow" @click="goToFeature('/voice-designer', 'Voice Designer')">
                <div class="designer-steps" aria-label="Voice Designer workflow">
                    <article class="designer-step active">
                        <span class="designer-step-icon">
                            <img src="@/assets/icons/iconMic.svg" alt="" />
                        </span>
                        <div>
                            <strong>Step 1</strong>
                            <p>Choose a Base Voice or upload</p>
                        </div>
                    </article>
                    <span class="designer-step-line" aria-hidden="true"></span>
                    <article class="designer-step">
                        <span class="designer-step-icon">
                            <img src="@/assets/icons/iconText.svg" alt="" />
                        </span>
                        <div>
                            <strong>Step 2</strong>
                            <p>Fine-Tune Your Voice with Prompt</p>
                        </div>
                    </article>
                </div>
                <div class="designer-console">
                    <section class="designer-base-card">
                        <h3>Base Voice</h3>
                        <div class="designer-voice-row">
                            <button aria-label="Play base voice" @click.stop="goToFeature('/voice-designer', 'Voice Designer')">
                                <span class="play-triangle light" aria-hidden="true"></span>
                            </button>
                            <div>
                                <strong>Isabella</strong>
                                <span>British voice - 8s</span>
                            </div>
                            <div class="designer-tags">
                                <span>British</span>
                                <span>Warm</span>
                            </div>
                        </div>
                        <div class="designer-tabs">
                            <span class="active">Voice Collection</span>
                            <span>Upload / Record</span>
                        </div>
                    </section>
                    <section class="designer-prompt-card">
                        <h3>Text Prompt</h3>
                        <div class="prompt-box">A calm, friendly narrator with a polished audiobook style.</div>
                        <div class="prompt-chips">
                            <span>Warm</span>
                            <span>Professional</span>
                            <span>Storytelling</span>
                        </div>
                        <button @click.stop="goToFeature('/voice-designer', 'Voice Designer')">Generate</button>
                    </section>
                    <section class="designer-history-card">
                        <h3>History</h3>
                        <div class="history-item">
                            <span>Warm narrator...</span>
                            <button aria-label="Play generated voice" @click.stop="goToFeature('/voice-designer', 'Voice Designer')">
                                <span class="play-triangle light" aria-hidden="true"></span>
                            </button>
                            <button aria-label="Download generated voice" @click.stop="goToFeature('/voice-designer', 'Voice Designer')">
                                <span class="download-icon" aria-hidden="true"></span>
                            </button>
                        </div>
                        <div class="designer-audio-bar">
                            <span></span>
                        </div>
                        <button class="save-voice-btn" @click.stop="goToFeature('/voice-designer', 'Voice Designer')">
                            Save Voice
                        </button>
                    </section>
                </div>
            </div>
        </section>

        <section id="studio" class="studio section-reveal">
            <div class="studio-copy">
                <p class="eyebrow">Text to Speech Studio</p>
                <h2>A production desk for long-form audio.</h2>
                <p>Manage pages, save line audio, reuse finished takes, and export the whole page when the script is
                    ready.</p>
            </div>
            <div class="tts-demo-shell" @click="goToFeature('/text-to-speech-projects', 'Text to Speech')">
                <div class="tts-demo-title">
                    <h3>Text-to-Speech Studio</h3>
                </div>
                <div class="tts-demo-panels">
                    <aside class="tts-project-demo">
                        <div class="project-selector-demo">Audiobook Draft</div>
                        <div class="page-tree-demo">
                            <div class="page-node-demo active">
                                <span>Page 1</span>
                                <button aria-label="Page 1 actions">...</button>
                                <div class="page-menu-demo" aria-label="Page actions">
                                    <span>Add</span>
                                    <span class="danger">Delete</span>
                                </div>
                            </div>
                            <div class="page-node-demo">
                                <span>Intro</span>
                                <button aria-label="Intro page actions">...</button>
                            </div>
                            <div class="page-node-demo nested">
                                <span>Scene Notes</span>
                                <button aria-label="Scene Notes page actions">...</button>
                            </div>
                        </div>
                        <button class="add-page-demo">Add New Page</button>
                    </aside>
                    <section class="tts-editor-demo">
                        <header class="editor-header-demo">
                            <button aria-label="Rename page">
                                <span class="pencil-icon" aria-hidden="true"></span>
                            </button>
                            <h3>Page 1</h3>
                            <span>Saved</span>
                            <button aria-label="Delete page">
                                <span class="trash-icon" aria-hidden="true"></span>
                            </button>
                        </header>
                        <div class="tts-lines-demo">
                            <article class="tts-line-demo active">
                                <div class="line-controls-demo">
                                    <span>1</span>
                                    <button aria-label="Play line 1">
                                        <span class="play-triangle" aria-hidden="true"></span>
                                    </button>
                                </div>
                                <p>Welcome to a faster way to create voice audio.</p>
                                <button class="audio-ready-demo" aria-label="Audio tag">
                                    <span>Audio</span><span>x</span>
                                </button>
                            </article>
                            <article class="tts-line-demo">
                                <div class="line-controls-demo">
                                    <span>2</span>
                                    <button aria-label="Play line 2">
                                        <span class="play-triangle" aria-hidden="true"></span>
                                    </button>
                                </div>
                                <p>Blend voices, tune delivery, and keep every take organized.</p>
                            </article>
                            <article class="tts-line-demo empty">
                                <div class="line-controls-demo">
                                    <span>3</span>
                                    <button aria-label="Play line 3">
                                        <span class="play-triangle" aria-hidden="true"></span>
                                    </button>
                                </div>
                                <p>Type your line here...</p>
                            </article>
                        </div>
                        <div class="textarea-toolbar-demo">
                            <span>118</span>
                            <button>Select</button>
                        </div>
                    </section>
                    <aside class="tts-settings-demo">
                        <div class="settings-tabs-demo">
                            <button class="active">Settings</button>
                            <button>History</button>
                        </div>
                        <label class="model-row-demo">
                            <span>Model:</span>
                            <strong>Basic</strong>
                        </label>
                        <div class="voice-table-demo">
                            <div class="voice-table-head">
                                <span>Voice</span>
                                <span>Weight</span>
                            </div>
                            <div class="voice-table-row">
                                <span>Adam</span>
                                <input value="1" readonly aria-label="Adam weight" />
                            </div>
                            <div class="voice-table-row muted">
                                <span>Alice</span>
                                <input value="0" readonly aria-label="Alice weight" />
                            </div>
                        </div>
                    </aside>
                </div>
                <div class="tts-audio-demo">
                    <button aria-label="Play page audio">
                        <span class="play-triangle muted" aria-hidden="true"></span>
                    </button>
                    <span>0:00 / 0:00</span>
                    <div><span></span></div>
                </div>
            </div>
        </section>

        <section id="about-preview" class="use-cases section-reveal">
            <div class="section-heading">
                <p class="eyebrow">Use cases</p>
                <h2>Built for the work people actually publish.</h2>
            </div>
            <div class="use-case-grid">
                <article>
                    <img src="@/assets/figs/home/home_audiobooks_2.png" alt="" />
                    <h3>Audiobooks</h3>
                    <p>Long-form narration with steady pacing and reusable voices.</p>
                </article>
                <article>
                    <img src="@/assets/figs/home/home_advertising_2.png" alt="" />
                    <h3>Advertising</h3>
                    <p>Short, expressive takes for campaigns, launches, and social clips.</p>
                </article>
                <article>
                    <img src="@/assets/figs/home/home_podcasting_2.png" alt="" />
                    <h3>Podcasting</h3>
                    <p>Clean production blocks for intros, segments, and audio experiments.</p>
                </article>
            </div>
        </section>

        <section id="pricing" class="pricing get-started-pricing section-reveal">
            <div class="get-started-panel">
                <div class="get-started-copy">
                    <p class="eyebrow">Get started</p>
                    <h2>Start simple. Scale when the voice work grows.</h2>
                    <p>Choose the plan that fits your first project. Sign in from the header when you are ready to start producing audio.</p>
                </div>
                <div class="home-pricing-preview">
                    <div class="home-subscription-grid" aria-label="Subscription plan overview">
                        <article v-for="plan in homePlans" :key="plan.baseName || plan.name"
                            class="home-subscription-card">
                            <div>
                                <h3>{{ plan.name }}</h3>
                                <p class="plan-description">{{ plan.description || 'Create voice projects with Timbrely tools.' }}</p>
                            </div>
                            <ul>
                                <li v-for="feature in plan.features" :key="feature">{{ feature }}</li>
                            </ul>
                        </article>
                    </div>
                </div>
            </div>
        </section>

        <section v-if="!isAuthenticated" class="register-cta" aria-label="Create a Timbrely account">
            <div class="register-cta-panel">
                <div class="register-cta-copy">
                    <p class="eyebrow">Create your workspace</p>
                    <h2>Register now and turn the first script into finished audio.</h2>
                    <p>Save projects, keep generated audio organized, and come back to the same voice workflow whenever you need it.</p>
                </div>
                <div class="register-cta-action-card">
                    <div class="register-cta-signal" aria-hidden="true">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <p>Start with one workspace for scripts, saved voices, and generated audio.</p>
                    <div class="register-cta-actions">
                        <button class="primary-link" @click="handleRegister">Register</button>
                        <button class="secondary-link" @click="handleLogin">Log in</button>
                    </div>
                </div>
            </div>
        </section>

        <HomeFooterView />
    </main>
</template>

<script>
import { inject, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import subscriptions from '@/data/subscriptions'
import subscriptionDescriptions from '@/data/SubscriptionDescriptions'
import TopHeader from '@/components/TopHeader.vue'
import HomeFooterView from '../components/home/HomeFooterView.vue'
import { createHomeMotionController } from '@/utils/homeMotion'
import { getVoiceProfilePicUrl } from '@/api/files'

export default {
    name: 'HomeIndex',
    components: {
        TopHeader,
        HomeFooterView,
    },
    setup() {
        const router = useRouter()
        const keycloak = inject('keycloak')
        const authToken = ref('')
        const heroVideoLoaded = ref(false)
        const heroVideo = ref(null)
        const scrollProgress = ref(0)
        const kokoroAvatar = (voiceName) => getVoiceProfilePicUrl('Kokoro v1.0', voiceName)

        const possibleVoices = [
            { name: 'George', description: 'British male', image: kokoroAvatar('bm_george') },
            { name: 'Sky', description: 'American female', image: kokoroAvatar('af_sky') },
            { name: 'Puck', description: 'American male', image: kokoroAvatar('am_puck') },
            { name: 'Fable', description: 'British male', image: kokoroAvatar('bm_fable') },
            { name: 'Onyx', description: 'American male', image: kokoroAvatar('am_onyx') },
        ]

        const descByName = Object.fromEntries(subscriptionDescriptions.map(d => [d.name, d]))
        const mappedPlans = subscriptions.filter(s => s.period === 'month').map(s => {
            const info = descByName[s.name] || { description: '', features: [] }
            return {
                name: s.name,
                baseName: s.name,
                period: s.period,
                price_id: s.price_id,
                price: s.price,
                description: info.description,
                features: info.features || [],
            }
        })

        const homePlans = computed(() => {
            return mappedPlans
        })

        const isAuthenticated = computed(() => authToken.value && authToken.value !== '')

        function checkAuthStatus() {
            authToken.value = localStorage.getItem('auth_token') || ''
        }

        function requireHomeAuth(action) {
            if (isAuthenticated.value) return true
            console.log(`Auth required for ${action}; redirecting to login...`)
            keycloak.login()
            return false
        }

        function goToFeature(path, action) {
            if (!requireHomeAuth(action)) return
            router.push({ path })
        }

        function handleRegister() {
            keycloak.register()
        }

        function handleLogin() {
            keycloak.login()
        }

        function onHeroVideoLoaded() {
            heroVideoLoaded.value = true
        }

        let cleanupHomeMotion = null

        onMounted(() => {
            checkAuthStatus()
            window.addEventListener('storage', checkAuthStatus)
            cleanupHomeMotion = createHomeMotionController({
                onProgress: value => {
                    scrollProgress.value = value
                },
            })
        })

        onUnmounted(() => {
            window.removeEventListener('storage', checkAuthStatus)
            if (cleanupHomeMotion) cleanupHomeMotion()
        })

        return {
            possibleVoices,
            kokoroAvatar,
            heroVideoLoaded,
            heroVideo,
            scrollProgress,
            homePlans,
            isAuthenticated,
            goToFeature,
            handleRegister,
            handleLogin,
            onHeroVideoLoaded,
        }
    }
}
</script>

<style scoped src="../assets/home-foundation.css"></style>
<style scoped src="../assets/home-studio-pricing.css"></style>
<style scoped src="../assets/home-responsive.css"></style>
